import { Schema } from '@google/genai';
import { Timestamp } from 'firebase/firestore';
import { NtaFile } from './file.typings';
import { DocumentChunkSource, FindingResponseSource, PccDataSource } from './source.typings';
import { TableColumn } from './table.typings';

export class GenerateDocumentDetails {
	relevantFindings?: AiFocusResponse[] = [];

	additionalDetails? = '';

	patientId?: string;

	facilityId?: string;

	filename?: string;

	originalFile?: Partial<NtaFile>;

	configId?: string;

	mappedProperties?: string[];
}

export class AiConfig {

	displayName: string;

	responseSchema: Schema;

	model: string;

	instructions: string;

	useInstructions: boolean;

	prompt: string;

	topK: number;

	topP: number;

	temperature: number;

	configId?: string;

	pccDocumentCategory?: string = 'Other';

	/**
	 * Include findings with one of these statuses in ai prompt.
	 * mappedProperties must also include relevantFindings.
	 * If not specified, findings will not be included in ai prompt.
	 */
	includedStatuses?: string[] = [];

	/**
	 * Focus groups to include findings from. If not specified, results will not be filtered by focus group.
	 * mappedProperties must also include relevantFindings.
	 * includedStatuses must also be set
	 */
	includedFocusGroupIds?: string[] = [];

	/**
	 * Foci to include findings from. If not specified, results will not be filted by focus.
	 * mappedProperties must also include relevantFindings.
	 * includedStatuses must also be set
	 */
	includedFocusIds?: string[] = [];

	source?: string;

	description: string;

	mappedProperties?: string[];

	allowFileAttachments?: boolean;

	primaryChunkSize: number;

	secondaryChunkSize: number;

	primaryChunkDelay: number;

	secondaryChunkDelay: number;

	relatedSourcesLimit?: number;

	relatedSourcesDistanceThreshold?: number;

	outputDimensionality?: number;

	adr: boolean;

	constructor(partial?: Partial<AiConfig>) {
		Object.assign(this, partial);
	}
}
export class FocusGroup {

	focusGroupId: string;

	focusGroupName: string;

	statusColumnStylesMap?: Record<string, { classes: string; icon: string }>;

	statusProperties: Array<{ label: string; colorClass?: string; icon?: string }> = [];

	sortOrder = 0;

	includeUnreviewed = false;

	constructor(partial?: Partial<FocusGroup>) {
		Object.assign(this, partial);

		this.statusColumnStylesMap = this.statusProperties.reduce((acc, { label, colorClass, icon }) => {
			acc[label] = { classes: colorClass, icon };
			return acc;
		}, {});
	}
}

export class AiFocus {

	scoreMap?: Record<string, number>;

	focusId = '';

	categories?: string[] = [];

	findingNameOptions?: string[] = [];

	instructions = '';

	label = '';

	shortLabel?: string;

	prompts: string[] = [];

	responseType: 'json' | 'html' = 'json';

	loading = false;

	requestedResponseCount?: number;

	actualResponseCount?: number;

	sortOrder = 0;

	archived = false;

	includeTextMatches = false;

	source: string | null = null;

	temperature: number;

	topK: number;

	topP: number;

	model: string;

	disableRegenerate? = false;

	focusGroupId? = 'Findings';

	includeEmpty? = false;

	facilityType? = 'SNF';

	responseSchema?: Schema;

	relatedSourcesLimit?: number;

	relatedSourcesDistanceThreshold?: number;

	constructor(partial?: Partial<AiFocus>) {
		Object.assign(this, partial);
	}
}

export class FindingResponseCode {
	code: string;

	description: string;

	primary = false;

	reason: string;
}

export class FindingResponse {
	category: string;

	name: string;

	description: string;

	sources: FindingResponseSource[] = [];

	score?: number;

	codes: FindingResponseCode[];

	mdsItems: string[];

	dosage?: string;

	prescribingPhysician?: string;

	/**
	 * status generated by AI model, not to be confused with Finding.status which is user-defined
	 */
	status?: string;

	severity?: string;

	medicationName? : string;

	constructor(partial?: Partial<FindingResponse>) {
		Object.assign(this, partial);
		if (this.sources) {
			this.sources = this.sources.map((source) => {
				if (source.sourceType === 'pccData') {
					return new PccDataSource(source as Partial<PccDataSource>);
				}
				return new DocumentChunkSource(source as Partial<DocumentChunkSource>);
			});
		}
	}
}

export class Finding {
	focusId: string;

	responseId: string;

	response: FindingResponse;

	/**
	 * status defined by user
	 */
	status?: string;

	isUnverified?: boolean;

	updatedBy?: string;

	updatedByName?: string;

	updatedDate?: Date;

	regenerating?: boolean;

	new?: boolean;

	constructor(partial?: Partial<Finding>) {
		Object.assign(this, partial);

		if (partial?.updatedDate instanceof Timestamp) {
			this.updatedDate = partial.updatedDate.toDate();
		}

		if (this.response) {
			this.response = new FindingResponse(this.response);
		}
	}
}

export class AiFocusResponse extends AiFocus {
	response: Finding[] | string = [];

	groupedResponses: Array<{ label: string; responses: Finding[] }> = [];

	regenerating = false;

	facilityId: string;

	patientId: string;

	tableColumns: TableColumn[];

	constructor(partial?: Partial<AiFocusResponse>) {
		super(partial);
		Object.assign(this, partial);
		if (this.response && Array.isArray(this.response)) {
			this.response = this.response.map((response) => new Finding(response));
		}
	}

}

export type AiFocusResponseWithFindings = Omit<AiFocusResponse, 'response'> & {
	response: Finding[];
};
